import { ApiError, api, errorMessage } from '../../api/client.ts'
import type { ExercisePatch } from '../../api/queries/exercises.ts'
import { patchHasBlank } from '../../api/queries/patchExercise.ts'
import { useEditorStore } from './editorStore.ts'

const DEBOUNCE_MS = 700
/**
 * A box she has emptied is not sent until she has stopped typing for this long (or leaves it): the
 * server refuses a blank, and she is usually just retyping. Blur, Publish and leaving still send it.
 */
const HOLD_BLANK_MS = 2000

/** What the server says of a save: a written key was saved and its re-mark is waiting for her to stop. */
type Saved = { remarkLater?: boolean } | undefined | void
type Saver = (exerciseId: string, patch: ExercisePatch) => Promise<Saved>

/**
 * The owner of every pending exercise save.
 *
 * This deliberately lives outside React. When the debounce timer belonged to the card, an
 * exercise unmounted within the debounce window -- she types and immediately clicks "All
 * tools" -- took its own pending save to the grave, and `editorStore.open()` then wiped the
 * buffer. Nothing in the UI reported it. The timers live here so that no component's
 * lifecycle can decide whether her text reaches the server.
 */

/** Injected by the editor page, which is the only place that has the mutation. */
let saver: Saver | null = null

const timers = new Map<string, ReturnType<typeof setTimeout>>()
const inFlight = new Set<string>()
/**
 * Written exercises whose key is saved but not yet re-marked against what students handed in. A key
 * is typed, and the 700 ms debounce saves it mid-word ("go", "has", "has gone"); re-marking at each
 * of those turned right answers wrong and then waiting for good. The re-mark is asked for only when
 * she has finished: on blur, on leaving the page, on closing it. A choice key is one click, so the
 * server re-marks it as it saves and never lands here.
 */
const rekey = new Set<string>()
/** Edited again while its own save was in flight; must run once more when that lands. */
const again = new Set<string>()
const running = new Map<string, Promise<void>>()
/** Bumped when an exercise's pending work is thrown away, so a late response is ignored. */
const generation = new Map<string, number>()
/** When each exercise was last typed into, for holding a blank box. */
const typedAt = new Map<string, number>()

export const setSaver = (fn: Saver | null) => {
	saver = fn
}

const patchFrom = (draft: {
	prompt?: string
	data?: unknown
	answer?: unknown
	explanation?: string
}) => {
	const body: ExercisePatch = {}
	if (draft.prompt !== undefined) body.prompt = draft.prompt
	if (draft.data !== undefined) body.data = draft.data
	if (draft.answer !== undefined) body.answer = draft.answer
	if (draft.explanation !== undefined) body.explanation = draft.explanation
	return body
}

async function run(exerciseId: string): Promise<void> {
	const store = useEditorStore.getState()
	const draft = store.drafts[exerciseId]
	if (!draft || !saver) return

	// A save is already on the wire. Do not drop this edit: remember it and re-run once the
	// in-flight one settles. Dropping it was how text could sit on "Unsaved" forever with no
	// request in flight and "Saving" stuck in the header.
	if (inFlight.has(exerciseId)) {
		again.add(exerciseId)
		return running.get(exerciseId)
	}

	inFlight.add(exerciseId)
	const version = store.beginSave(exerciseId)
	const body = patchFrom(draft)
	const gen = generation.get(exerciseId) ?? 0
	const stale = () => (generation.get(exerciseId) ?? 0) !== gen

	const promise = (async () => {
		try {
			const saved = await saver?.(exerciseId, body)
			if (saved?.remarkLater && !stale()) rekey.add(exerciseId)
			// `settle` keeps the draft if she typed again while this was in flight, so the
			// newer text is never replaced by the response to an older request.
			if (!stale()) useEditorStore.getState().settle(exerciseId, version)
		} catch (err) {
			const refused = err instanceof ApiError && err.status === 400
			const draftNow = useEditorStore.getState().drafts[exerciseId]
			if (refused && draftNow && draftNow.version !== version) {
				// She typed after this (blank) version went out: the refusal is about text she has
				// already replaced. Clear this save and let her newer text go out on its own.
				if (!stale()) useEditorStore.getState().settle(exerciseId, version)
				again.add(exerciseId)
			} else {
				if (!stale()) useEditorStore.getState().fail(exerciseId, errorMessage(err), refused)
				// A failed save keeps the draft; do not spin on it, she retries explicitly.
				again.delete(exerciseId)
			}
		} finally {
			inFlight.delete(exerciseId)
			running.delete(exerciseId)
		}
		if (again.delete(exerciseId) && useEditorStore.getState().drafts[exerciseId]) {
			await run(exerciseId)
		}
	})()

	running.set(exerciseId, promise)
	return promise
}

/**
 * Throws away everything pending for these exercises: the timer, a queued re-run, the draft, and
 * the outcome of a request already on the wire. For a deleted exercise, or "Leave anyway".
 */
export function cancelExercises(ids: string[]) {
	for (const id of ids) {
		clearTimeout(timers.get(id))
		timers.delete(id)
		again.delete(id)
		rekey.delete(id)
		typedAt.delete(id)
		generation.set(id, (generation.get(id) ?? 0) + 1)
		useEditorStore.getState().discard(id)
	}
}

export const cancelAll = () =>
	cancelExercises([
		...Object.keys(useEditorStore.getState().drafts),
		...inFlight,
		...timers.keys(),
		...rekey,
	])

/** Call on every keystroke. */
export function scheduleSave(exerciseId: string) {
	typedAt.set(exerciseId, Date.now())
	arm(exerciseId, DEBOUNCE_MS)
}

function arm(exerciseId: string, ms: number) {
	clearTimeout(timers.get(exerciseId))
	timers.set(
		exerciseId,
		setTimeout(() => {
			timers.delete(exerciseId)
			const draft = useEditorStore.getState().drafts[exerciseId]
			const quiet = (typedAt.get(exerciseId) ?? 0) + HOLD_BLANK_MS - Date.now()
			// Still mid-retype: look again when she has been quiet long enough. The draft stays, so
			// nothing is lost, and a typed character or a blur takes over before then.
			if (draft && patchHasBlank(draft) && quiet > 0) return arm(exerciseId, quiet)
			typedAt.delete(exerciseId)
			void run(exerciseId)
		}, ms),
	)
}

/**
 * Asks the server to re-mark this exercise's handed-in answers against the key it has saved. Only
 * the commit points call it. A failure keeps the exercise waiting for the next commit point.
 */
async function commitKey(exerciseId: string): Promise<void> {
	if (!rekey.delete(exerciseId)) return
	try {
		await saver?.(exerciseId, { remark: true })
	} catch {
		rekey.add(exerciseId)
	}
}

/** Call on blur, on an explicit retry, and whenever a change is structural rather than typed. */
export async function flushExercise(exerciseId: string): Promise<void> {
	clearTimeout(timers.get(exerciseId))
	timers.delete(exerciseId)
	await run(exerciseId)
	await commitKey(exerciseId)
}

/**
 * Everything outstanding, awaited. The router blocker calls this before letting a navigation
 * through, which is the only reason a mid-sentence edit survives clicking a link.
 */
export async function flushAll(): Promise<void> {
	for (const t of timers.values()) clearTimeout(t)
	timers.clear()
	const ids = new Set([...Object.keys(useEditorStore.getState().drafts), ...inFlight])
	await Promise.all([...ids].map((id) => run(id)))
	await Promise.all([...rekey].map(commitKey))
}

/** A written key is saved and its re-mark has not been asked for yet. */
export const hasPendingRemark = () => rekey.size > 0

/** Is there anything typed that the server has not accepted yet? */
export const hasPendingWork = () => {
	const s = useEditorStore.getState()
	return Object.keys(s.drafts).length > 0 || inFlight.size > 0
}

/**
 * Last resort when the page is going away: a normal request dies with it, `keepalive` does not.
 * Drafts stay in the store; if the page is restored from the back-forward cache they save again.
 */
export function flushOnPageHide() {
	for (const t of timers.values()) clearTimeout(t)
	timers.clear()
	for (const [id, draft] of Object.entries(useEditorStore.getState().drafts)) {
		const body = patchFrom(draft)
		// Closing is finishing: a key in this draft is saved and re-marked by the one request.
		if (body.answer !== undefined || rekey.has(id)) body.remark = true
		rekey.delete(id)
		sendKeepalive(id, body).catch(() => {})
	}
	commitPendingRemarks()
}

const sendKeepalive = (id: string, json: ExercisePatch) =>
	api.exercises[':id'].$patch({ param: { id }, json }, { init: { keepalive: true } })

/**
 * Commits the keys that are saved but not yet re-marked, as a request that outlives the page. Sent when
 * the tab goes to the background as well as at `pagehide`: a phone can kill a hidden tab without ever
 * firing `pagehide`, and a re-mark waiting only for blur or leaving would be lost. A request that
 * fails puts its key back, so the next blur or hide asks again.
 */
export function commitPendingRemarks() {
	for (const id of rekey) {
		sendKeepalive(id, { remark: true }).catch(() => rekey.add(id))
	}
	rekey.clear()
}
